import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { activateDeal } from "@/api/deals-api";
import { useCurrentDealStore } from "@/store/current-deal-store";
import { useDealStatsStore } from "@/store/deal-stats-store";
import { getErrorMessage } from "@/lib/api-error";
import { parseApiDate } from "@/lib/utils";
import type { DealTransformed } from "@/lib/schema/deal-schema";
import type { ReportItemConfig } from "@/components/deal-detail/report-types";

export const OPTIMISTIC_VERSION_ID = "__optimistic__";

export interface PanelVersion {
  /** Real `_id` from the API, or `OPTIMISTIC_VERSION_ID` for an in-flight generation. */
  id: string;
  /** 1-based index: oldest = 1, newest = versions.length. */
  versionNumber: number;
  createdOn: string | Date | null;
  /** Display text shown beside `v{n}` in the dropdown row. */
  generatedAt: string;
  /** True when this entry is an optimistic placeholder for an in-flight generation. */
  generating: boolean;
}

interface UseReportPanelArgs {
  item: ReportItemConfig;
  currentDeal: DealTransformed;
  workspaceId: string | null;
  reportStatus: unknown;
  initialVersionId?: string;
  onClose: () => void;
}

interface UseReportPanelResult {
  versions: PanelVersion[];
  selectedVersionId: string;
  setSelectedVersionId: (id: string) => void;
  isGenerating: boolean;
  isDeleting: boolean;
  hasError: boolean;
  canGenerate: boolean;
  canDelete: boolean;
  optimistic: PanelVersion | null;
  generateNew: () => void;
  deleteSelected: () => void;
  clearError: () => void;
}

const formatDate = (d: string | Date | null): string => {
  if (!d) return "";
  try {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(parseApiDate(d));
  } catch {
    return "";
  }
};

const extractReportStatus = (
  reportStatus: unknown,
  statusKey: string
): string | undefined => {
  if (!reportStatus || typeof reportStatus !== "object") return undefined;
  const rs = reportStatus as Record<string, unknown>;
  const app = rs.opportunityApplication as Record<string, unknown> | undefined;
  const reports = app?.report_status as Record<string, string> | undefined;
  return reports?.[statusKey];
};

const isActiveStatus = (value: string | undefined): boolean =>
  value === "in_progress" ||
  // Worker parks newly-queued reports here when something else is in flight
  // for the same deal; FE treats it as still-active so the spinner stays.
  value === "queued" ||
  // Legacy token still written for some report types (tear-sheet / metrics /
  // startupscore check both spellings in their inline components).
  value === "inprogress" ||
  value === "pending" ||
  (typeof value === "string" && value.startsWith("generating_"));

const isFailedStatus = (value: string | undefined): boolean => value === "failed";

export function useReportPanel({
  item,
  currentDeal,
  workspaceId,
  reportStatus,
  initialVersionId,
  onClose,
}: UseReportPanelArgs): UseReportPanelResult {
  const { updateDeal } = useCurrentDealStore();
  const { fetchDealStats } = useDealStatsStore();
  const queryClient = useQueryClient();

  const [selectedVersionId, setSelectedVersionId] = useState<string>(
    initialVersionId ?? ""
  );
  // Keep the selection in sync with the `initialVersionId` prop so that
  // when the reports-tab dropdown (or anything else upstream) pushes a
  // different versionId into an already-open panel, we switch to it
  // instead of staying stuck on the first-mount selection.
  useEffect(() => {
    if (!initialVersionId) return;
    if (initialVersionId === selectedVersionId) return;
    setSelectedVersionId(initialVersionId);
    // Intentionally excluding selectedVersionId from the deps: we only
    // react to external prop changes here. Internal changes from the
    // in-panel version dropdown shouldn't retrigger this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialVersionId]);
  const [optimistic, setOptimistic] = useState<PanelVersion | null>(null);
  const [hasError, setHasError] = useState(false);

  // Build the versions array from aiGenerations, newest-first.
  const realVersions = useMemo<PanelVersion[]>(() => {
    if (item.skipVersions) return [];
    const gen = currentDeal.aiGenerations as Record<string, unknown>;
    const raw = gen?.[item.dataKey];
    if (!Array.isArray(raw)) return [];
    const validated = item.isReportValid ? raw.filter(item.isReportValid) : raw;
    const sorted = (validated as Array<{ _id?: string; created_on?: string | Date }>)
      .filter((v) => v._id)
      .sort((a, b) => {
        const ad = a.created_on ? new Date(a.created_on).getTime() : 0;
        const bd = b.created_on ? new Date(b.created_on).getTime() : 0;
        return bd - ad;
      });
    const count = sorted.length;
    return sorted.map((v, idx) => ({
      id: v._id as string,
      versionNumber: count - idx,
      createdOn: v.created_on ?? null,
      generatedAt: formatDate(v.created_on ?? null),
      generating: false,
    }));
  }, [currentDeal.aiGenerations, item.dataKey, item.isReportValid, item.skipVersions]);

  const versions = useMemo<PanelVersion[]>(() => {
    if (!optimistic) return realVersions;
    return [optimistic, ...realVersions];
  }, [optimistic, realVersions]);

  // Default selection to newest when nothing is selected or the selection no
  // longer exists (e.g. after a delete).
  useEffect(() => {
    if (item.skipVersions) return;
    if (
      selectedVersionId &&
      versions.some((v) => v.id === selectedVersionId)
    ) {
      return;
    }
    const fallback = versions[0]?.id ?? "";
    if (fallback !== selectedVersionId) {
      setSelectedVersionId(fallback);
    }
  }, [versions, selectedVersionId, item.skipVersions]);

  const statusValue = extractReportStatus(
    reportStatus,
    item.statusKey ?? item.dataKey
  );
  const statusActive = isActiveStatus(statusValue);
  const statusFailed = isFailedStatus(statusValue);

  // Drop the optimistic row once a newer real version has arrived. We compare
  // by count because the new _id only exists after the invalidated query
  // refetches — versionNumber is an easier signal than diffing IDs.
  const prevRealCountRef = useRef(realVersions.length);
  useEffect(() => {
    const prev = prevRealCountRef.current;
    prevRealCountRef.current = realVersions.length;
    if (!optimistic) return;
    if (realVersions.length > prev) {
      setOptimistic(null);
      setHasError(false);
      // Auto-advance selection to the just-arrived real version (newest).
      const newest = realVersions[0]?.id;
      if (newest) setSelectedVersionId(newest);
    }
  }, [realVersions, optimistic]);

  // Drop optimistic + surface the error banner when generation fails.
  useEffect(() => {
    if (!optimistic) return;
    if (statusFailed) {
      setOptimistic(null);
      setHasError(true);
    }
  }, [statusFailed, optimistic]);

  // Close the panel once the report's last version is gone. The panel's own
  // delete handles this inline, but an external delete (e.g. the reports-tab
  // list mutating the same aiGenerations array) leaves the panel mounted with
  // nothing to show. Guarded by `sawVersionsRef` so we never close during the
  // initial mount (before data lands) and skipped while a generation is in
  // flight.
  const sawVersionsRef = useRef(false);
  useEffect(() => {
    if (item.skipVersions) return;
    if (realVersions.length > 0) {
      sawVersionsRef.current = true;
      return;
    }
    if (sawVersionsRef.current && !optimistic && !statusActive) {
      onClose();
    }
  }, [realVersions.length, optimistic, statusActive, item.skipVersions, onClose]);

  const generateMutation = useMutation({
    mutationFn: async () => {
      if (!workspaceId) throw new Error("No workspace selected");

      const wasActivated = currentDeal.aiTracked === true;
      if (item.requiresActivation && !wasActivated) {
        const activation = await activateDeal(workspaceId, currentDeal.id);
        updateDeal({
          aiTracked: true,
          overview: {
            ...currentDeal.overview,
            status: activation.data.newStatus || currentDeal.overview.status,
          },
        });
        await fetchDealStats(workspaceId);
      }

      return item.triggerGenerate({ currentDeal, workspaceId });
    },
    onSuccess: () => {
      setHasError(false);
      // Only push an optimistic placeholder when the version dropdown is
      // visible — `skipVersions` types (valuations, deal-memo) don't render
      // the dropdown, and valuations completes synchronously without a
      // `report_status.valuations` transition, so the placeholder would
      // never be dropped.
      if (!item.skipVersions) {
        const nextNumber = realVersions.length + 1;
        setOptimistic({
          id: OPTIMISTIC_VERSION_ID,
          versionNumber: nextNumber,
          createdOn: new Date().toISOString(),
          generatedAt: "just now",
          generating: true,
        });
      }
      toast.success(`${item.title} generation started`);
      queryClient.invalidateQueries({
        queryKey: ["reportStatus", currentDeal.id, workspaceId],
      });
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, `Failed to generate ${item.title}`));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (versionId: string) => {
      if (!workspaceId) throw new Error("No workspace selected");
      await item.deleteVersion({ currentDeal, workspaceId }, versionId);
      return versionId;
    },
    onSuccess: (versionId) => {
      // `tearsheets_all` is derived from `tearsheets_v2` (rebuilt by the deal
      // store on every aiGenerations update), so delete from the source array.
      const gen = currentDeal.aiGenerations as Record<string, unknown>;
      const sourceKey =
        item.dataKey === "tearsheets_all" ? "tearsheets_v2" : item.dataKey;
      const arr = Array.isArray(gen[sourceKey])
        ? (gen[sourceKey] as Array<{ _id?: string }>)
        : [];
      const next = arr.filter((r) => r._id !== versionId);
      updateDeal({
        aiGenerations: {
          ...currentDeal.aiGenerations,
          [sourceKey]: next,
        } as typeof currentDeal.aiGenerations,
      });
      toast.success(`${item.title} version deleted`);
      // If we just removed the only remaining real version, close the panel —
      // mirrors the prototype's "delete last closes panel" behaviour.
      if (next.length === 0 && !optimistic) {
        onClose();
      }
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to delete"));
    },
  });

  const isGenerating = statusActive || generateMutation.isPending || !!optimistic;

  const canGenerate = !!workspaceId && !item.skipGenerate && !isGenerating;

  const selectedIsReal =
    !!selectedVersionId && selectedVersionId !== OPTIMISTIC_VERSION_ID;
  const canDelete =
    !item.skipDelete && selectedIsReal && !deleteMutation.isPending && !isGenerating;

  return {
    versions,
    selectedVersionId,
    setSelectedVersionId,
    isGenerating,
    isDeleting: deleteMutation.isPending,
    hasError,
    canGenerate,
    canDelete,
    optimistic,
    generateNew: () => generateMutation.mutate(),
    deleteSelected: () => {
      if (!canDelete) return;
      deleteMutation.mutate(selectedVersionId);
    },
    clearError: () => setHasError(false),
  };
}
